import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, Square, Euro, MapPin, Building2, Loader2, X, ChevronLeft, ChevronRight, Compass, Printer, ImageIcon, Phone } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import ContactForm from '../components/ContactForm';
import StickyDetailsLayout from '../components/apartment/StickyDetailsLayout';
import { useBuildingConfig } from '../lib/hooks/useBuildingConfig';
import { usePageLayouts } from '../lib/hooks/usePageLayouts';
import { getExposureText } from '../lib/exposure';

interface Apartment {
  id: string;
  number: string;
  floor: number;
  entrance: string;
  rooms: number;
  area: number;
  netArea?: number;
  price: number;
  promoPrice?: number;
  status: 'available' | 'reserved' | 'sold';
  description: string;
  features: string[];
  images: string[];
  mainImage?: string;
  hasTerrace: boolean;
  exposure: string;
  brochureUrl?: string;
  model3d?: { aiInteriors?: string[] };
}

const defaultImages = [
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=800&q=80'
];

const ApartmentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getEntranceLabel } = useBuildingConfig();
  const { layouts } = usePageLayouts();
  const [apartment, setApartment] = useState<Apartment | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLightbox, setShowLightbox] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);

  const returnFilters = searchParams.get('returnFilters') || '';

  useEffect(() => {
    const fetchApartment = async () => {
      if (!id) return;
      
      try {
        const docRef = doc(db, 'apartments', id);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setApartment({ id: docSnap.id, ...docSnap.data() } as Apartment);
        }
      } catch (error) {
        console.error('Error fetching apartment:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApartment();
  }, [id]);

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setShowLightbox(true);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === 0 ? displayImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => 
      prev === displayImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!showLightbox) return;

    if (e.key === 'Escape') {
      setShowLightbox(false);
    } else if (e.key === 'ArrowLeft') {
      setCurrentImageIndex((prev) => 
        prev === 0 ? displayImages.length - 1 : prev - 1
      );
    } else if (e.key === 'ArrowRight') {
      setCurrentImageIndex((prev) => 
        prev === displayImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!apartment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-800">Апартаментът не е намерен</h2>
          <button
            onClick={() => {
              const path = returnFilters ? `/apartments?${returnFilters}` : '/apartments';
              navigate(path);
            }}
            className="mt-4 inline-flex items-center text-primary hover:text-primary-dark"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Обратно към списъка
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: Apartment['status']) => {
    switch (status) {
      case 'available':
        return 'bg-accent text-white';
      case 'reserved':
        return 'bg-orange-500 text-white';
      case 'sold':
        return 'bg-red-500 text-white';
      default:
        return 'bg-neutral-200 text-neutral-700';
    }
  };

  const getStatusText = (status: Apartment['status']) => {
    switch (status) {
      case 'available':
        return 'Свободен';
      case 'reserved':
        return 'Резервиран';
      case 'sold':
        return 'Продаден';
      default:
        return '';
    }
  };

  const displayImages = apartment.images?.length > 0 ? apartment.images : defaultImages;
  const mainImage = apartment.mainImage || displayImages[0];
  const additionalImages = displayImages.filter(img => img !== mainImage).slice(0, 2);
  const backPath = returnFilters ? `/apartments?${returnFilters}` : '/apartments';

  // Desktop scrolls to the inline form; mobile opens it as a modal.
  const handleContact = () => {
    if (window.innerWidth < 768) {
      setShowContactModal(true);
    } else {
      document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Shared by both layouts so the lightbox and mobile modal behave identically.
  const overlays = (
    <>
      {showContactModal && (
        <ContactForm
          apartmentNumber={apartment.number}
          apartmentEntrance={apartment.entrance}
          isModal={true}
          onClose={() => setShowContactModal(false)}
        />
      )}

      {showLightbox && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={() => setShowLightbox(false)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setShowLightbox(false)}
          >
            <X className="w-8 h-8" />
          </button>

          <button
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
            onClick={handlePrevImage}
          >
            <ChevronLeft className="w-12 h-12" />
          </button>

          <img
            src={displayImages[currentImageIndex]}
            alt={`${apartment.number} - изглед ${currentImageIndex + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
            onClick={handleNextImage}
          >
            <ChevronRight className="w-12 h-12" />
          </button>

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white">
            {currentImageIndex + 1} / {displayImages.length}
          </div>
        </div>
      )}
    </>
  );

  if (layouts.apartmentDetails === 'sticky') {
    return (
      <>
        <StickyDetailsLayout
          apartment={apartment}
          images={displayImages}
          mainImage={mainImage}
          entranceLabel={getEntranceLabel(apartment.entrance)}
          statusText={getStatusText(apartment.status)}
          statusColor={getStatusColor(apartment.status)}
          exposureText={getExposureText(apartment.exposure)}
          backPath={backPath}
          onImageClick={handleImageClick}
          onContact={handleContact}
        />
        <div className="pb-16 bg-neutral-50">
          <div className="container-custom">
            <div id="contact-form" className="hidden md:block">
              <ContactForm
                apartmentNumber={apartment.number}
                apartmentEntrance={apartment.entrance}
              />
            </div>
          </div>
        </div>
        {overlays}
      </>
    );
  }

  return (
    <>
      <div className="pt-24 pb-16 bg-neutral-50">
        <div className="container-custom">
          <button
            onClick={() => {
              const path = returnFilters ? `/apartments?${returnFilters}` : '/apartments';
              navigate(path);
            }}
            className="inline-flex items-center text-primary hover:text-primary-dark mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Обратно към списъка
          </button>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 lg:gap-12 mb-16">
            <div>
              <div className="bg-white rounded-xl overflow-hidden shadow-lg">
                <div className="relative cursor-pointer" onClick={() => handleImageClick(0)}>
                  <img
                    src={mainImage}
                    alt={`Апартамент ${apartment.number}`}
                    className="w-full h-auto hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <ImageIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6">
                {additionalImages.map((image, index) => (
                  <div key={index} className="bg-white rounded-xl overflow-hidden shadow-lg group">
                    <div 
                      className="relative cursor-pointer overflow-hidden" 
                      onClick={() => handleImageClick(index + 1)}
                    >
                      <img
                        src={image}
                        alt={`Апартамент ${apartment.number} - изглед ${index + 2}`}
                        className="w-full h-auto group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg">
                <div className="flex justify-between items-start mb-6">
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{apartment.number}</h1>
                  <div className="flex items-center gap-2">
                    <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(apartment.status)}`}>
                      {getStatusText(apartment.status)}
                    </span>
                    {apartment.brochureUrl && (
                      <a
                        href={apartment.brochureUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-primary hover:text-primary-dark transition-colors"
                        title="Принтирай брошура"
                      >
                        <Printer className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-8">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Етаж</p>
                      <p className="font-semibold text-gray-900">{apartment.floor}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Вход</p>
                      <p className="font-semibold text-gray-900">{getEntranceLabel(apartment.entrance)}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                      <Home className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Стаи</p>
                      <p className="font-semibold text-gray-900">{apartment.rooms}</p>
                    </div>
                  </div>
                  {apartment.netArea && (
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                        <Square className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-neutral-600">Чиста площ</p>
                        <p className="font-semibold text-gray-900">{apartment.netArea} м²</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                      <Square className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Обща площ</p>
                      <p className="font-semibold text-gray-900">{apartment.area} м²</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                      <Compass className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Изложение</p>
                      <p className="font-semibold text-gray-900">{getExposureText(apartment.exposure)}</p>
                    </div>
                  </div>
                </div>

                {apartment.description && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900">Описание</h2>
                    <p className="text-neutral-600 leading-relaxed">{apartment.description}</p>
                  </div>
                )}

                {apartment.features && apartment.features.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900">Характеристики</h2>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {apartment.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-neutral-600">
                          <span className="w-2 h-2 bg-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                          <span className="leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {apartment.price > 0 && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl">
                    <div>
                      <p className="text-sm text-neutral-600">Цена</p>
                      <div className="flex items-center gap-3 mt-1">
                        {apartment.promoPrice && apartment.promoPrice < apartment.price ? (
                          <>
                            <span className="text-xl lg:text-2xl text-gray-500 line-through">€{apartment.price.toLocaleString()}</span>
                            <span className="text-2xl lg:text-3xl font-bold text-red-600">€{apartment.promoPrice.toLocaleString()}</span>
                          </>
                        ) : (
                          <span className="text-2xl lg:text-3xl font-bold text-gray-900">€{apartment.price.toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                    <button
                      className="w-full sm:w-auto bg-primary text-white px-8 py-4 rounded-xl hover:bg-primary-dark transition duration-300 font-medium shadow-lg hover:shadow-xl"
                      disabled={apartment.status === 'sold'}
                      onClick={handleContact}
                    >
                      Свържете се с нас
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {apartment.model3d?.aiInteriors && apartment.model3d.aiInteriors.length > 0 && (
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <ImageIcon className="w-6 h-6 text-primary mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">AI интериор</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {apartment.model3d.aiInteriors.map((url, i) => (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-xl overflow-hidden shadow-lg group block"
                  >
                    <img
                      src={url}
                      alt={`AI интериор ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </a>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-3">
                Примерна визуализация на обзавеждането, генерирана с изкуствен интелект.
              </p>
            </div>
          )}

          <div id="contact-form" className="hidden md:block">
            <ContactForm
              apartmentNumber={apartment.number}
              apartmentEntrance={apartment.entrance}
            />
          </div>
        </div>
      </div>

      {overlays}
    </>
  );
};

export default ApartmentDetailsPage;